import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SEO, Reveal, Eyebrow } from "@/components/motion";

const VALUES = [
  { n: "01", title: "Time", text: "Your time matters." },
  { n: "02", title: "Home", text: "Your space deserves to feel cared for." },
  { n: "03", title: "Professional Care", text: "We take pride in doing things properly." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <SEO
        title="About Us | HOMELARA - Professional Cleaning Company in London"
        description="HOMELARA is a professional cleaning company in London built on one idea: a clean space should give something back to you. Time, peace of mind, and a home that feels cared for."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-44 pb-16">
        <Reveal>
          <Eyebrow>About us</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">
            Cleaning with a little more care.
          </h1>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="space-y-6 text-base sm:text-lg leading-relaxed text-body">
              <p className="font-serif text-2xl sm:text-3xl text-ink">HOMELARA was created with a simple idea:</p>
              <p className="font-serif text-2xl sm:text-3xl italic text-blue">
                A clean space should give something back to you.
              </p>
              <p>
                It should give you time. It should give you peace of mind. It should make your home or
                workplace feel better.
              </p>
              <p>We believe professional cleaning should feel personal.</p>
              <p>
                Our goal is not simply to complete a checklist. It is to leave your space feeling fresh,
                cared for and ready for you to enjoy.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="img-frame aspect-[4/5] overflow-hidden rounded-3xl">
              <img
                src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1400&q=80"
                alt="Elegant, cared-for London home interior styled with natural textures"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-warmcard border-y border-subtle">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <Reveal>
            <Eyebrow>Our values</Eyebrow>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-ink">
              Three words we work by.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-subtle bg-surface p-9">
                  <span className="font-mono text-xs tracking-[0.3em] text-sage-dark">{v.n}</span>
                  <h3 className="mt-4 font-serif text-3xl tracking-tight text-blue">{v.title}</h3>
                  <p className="mt-3 text-body">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <Reveal className="max-w-2xl">
          <Eyebrow>Why the name</Eyebrow>
          <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink">HOMELARA.</h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-body">
            The name begins where everything begins: <em className="font-serif italic text-blue">home</em>.
            The rest is personal - and we like it that way. What matters is what it stands for: a space
            that feels cared for, and the time you get back to enjoy it.
          </p>
        </Reveal>
        <Reveal className="mt-16">
          <Link
            to="/book"
            data-testid="about-book-button"
            className="inline-flex items-center gap-2 rounded-full bg-blue px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] text-canvas transition-all duration-300 hover:bg-blue-hover hover:-translate-y-0.5"
          >
            Book a Clean <ArrowUpRight size={15} />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
